import { get } from "lodash-es";
import translations, { type LocaleTranslations, type TranslationMap } from "../i18n/translations";
import { setContext, getContext, hasContext } from "svelte";
import type { LayoutSettings } from "../types";
import { Status } from "../types";

const defaultLocale = new Intl.Locale("en-US");
const defaultLanguage = defaultLocale.language;

const contextKey = "locale";
const overridesKey = "locale-overrides";

export function getLocale() {
  if (hasContext(contextKey)) {
    return getContext<Intl.Locale>(contextKey);
  } else {
    return defaultLocale;
  }
}
export function getOverrides(): LocaleTranslations {
  if (hasContext(overridesKey)) {
    return getContext<LocaleTranslations>(overridesKey);
  } else {
    return {};
  }
}
export function setLanguage(language: Intl.UnicodeBCP47LocaleIdentifier, settings: LayoutSettings) {
  try {
    const locale = new Intl.Locale(language);

    setContext(contextKey, locale);

    setContext(overridesKey, {
      status: {
        [Status.ACTIVE]: settings.activeStatusLabel,
        [Status.INACTIVE]: settings.inactiveStatusLabel,
        [Status.ON_HOLD]: settings.onHoldStatusLabel,
        [Status.OUT_LATE]: settings.outLateStatusLabel,
        [Status.CLOSED_TEMPORARILY]: settings.closedTemporarilyStatusLabel,
        [Status.CLOSED_SEASONALLY]: settings.closedSeasonallyStatusLabel,
        [Status.CLOSED_PERMANENTLY]: settings.closedPermanentlyStatusLabel
      }
    });
  } catch (error) {
    console.error("Invalid language code:", language, error);

    setContext(contextKey, new Intl.Locale("en-US"));
  }
}

export type Variables = {
  [key: string]: string | number;
};

export type Locale = {
  [key: string]: string;
};

export function translate(
  locale: Intl.Locale,
  overrides: LocaleTranslations,
  key: string,
  vars: Variables | null
): string {
  // Grab the translation from the translations object.
  let text: string | TranslationMap | undefined =
    get(overrides, key) ||
    get(translations[locale.language], key) ||
    get(translations[defaultLanguage], key);

  if (text == null) {
    return key;
  }

  if (typeof text != "string") {
    throw new Error("Key path did not return a string: " + key);
  }

  // Replace any passed in variables in the translation string.
  if (vars) {
    Object.keys(vars).map((k) => {
      const regex = new RegExp(`{{${k}}}`, "g");

      text = (text as string).replace(regex, `${vars[k]}`);
    });
  }

  return text as string;
}
